import { DateTime } from 'luxon'

export const yupEmailRegex =
    /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)])/gim

export function regexInitials(text?: string): string {
    const parts = (text || '').toLowerCase().match(/\b(\w)/g)
    return parts[0] + parts.pop()
}

export function regexDigitsOnly(text?: string): string {
    return (text || '').replace(/\D/g, '')
}

export function regexSearchable(text?: string): string {
    return (text || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[^a-z\d]/gi, '')
}

export function formatTemplateDocumentName(text: string = '%{yyyyLLdd} - ', variables: object = {}): string {
    // Find all instances that represent a variable or date, such as ${file.id} or %{YYYY}.
    const matches = (text || '').matchAll(/([$%]){([\w\d.]+)}/gim)
    let match = matches.next()
    // noinspection JSAssignmentUsedAsCondition
    while (!match.done) {
        let replacement: string | number
        const [search, type, token]: [string, string, string] = match.value
        switch (type) {
            // Date match, such as %{YYYY}.
            case '%':
                replacement = DateTime.now().toFormat(token)
                break
            // Variable match, such as ${file.id}.
            case '$':
            default:
                let section: object | string = variables
                const parts: string[] = token.split('.').filter(part => part.length > 0)
                // It will just show the token if there is no match.
                replacement = token

                for (let i = 0; i < parts.length; i++) {
                    // Do not proceed if there is no match for the requested variable.
                    if (typeof section !== 'object' || !section.hasOwnProperty(parts[i])) {
                        break
                    }
                    // Traverse the variables object by going one level deeper.
                    section = section[parts[i]]
                    // Only set the replacement when we are at the end of the array, and we have a replaceable
                    // value.
                    if (parts.length === i + 1 && (typeof section === 'number' || typeof section === 'string')) {
                        replacement = section
                    }
                }
        }
        text = text.replace(search, replacement.toString())
        match = matches.next()
    }
    return text
}

export function formatInputFolderName(text: string): string {
    return formatInputDocumentName(text).replace(/(\.+$)/gi, '')
}

export function formatInputDocumentName(text: string, extension: string = null): string {
    const suffix: string = extension ? `.${extension}` : ''
    return (
        text
            .replace(/(^\s+)/gi, '')
            .replace(/(\s+$)/gi, '')
            .replace(/["*:<>?\/\\|]/gi, '')
            .replace(/(\s{2,})/gi, ' ') + suffix
    )
}

export function formatMultipleEmailAddresses(inputValue: string): string[] | undefined {
    return inputValue
        ?.replace(',', ';')
        .split(';')
        .filter(value => !!value)
        .map(value => value?.trim())
        .map(value => {
            const result = value.match(yupEmailRegex)
            return result?.[0] || value
        })
        .map(value => value?.trim())
        .filter(value => !!value && value.length > 0)
}

/**
 * Matches if text is an email address.
 * @param {string} text The text to match.
 * @return boolean If the text matches as email address.
 */
export function regexIsEmail(text: string): boolean {
    return yupEmailRegex.test(text)
}

/**
 * Matches if text is an EU VAT number. Contains all EU countries as of 2018, except Croatia.
 * @see https://www.oreilly.com/library/view/regular-expressions-cookbook/9781449327453/ch04s21.html.
 * @param {string} text The text to match.
 * @return boolean If the text matches as VAT number.
 */
export function regexIsVatNumber(text: string): boolean {
    const countries: string[] = [
        '(AT)?U[0-9]{8}', // Austria
        '(BE)?0[0-9]{9}', // Belgium
        '(BG)?[0-9]{9,10}', // Bulgaria
        '(CY)?[0-9]{8}L', // Cyprus
        '(CZ)?[0-9]{8,10}', // Czech Republic
        '(DE)?[0-9]{9}', // Germany
        '(DK)?[0-9]{8}', // Denmark
        '(EE)?[0-9]{9}', // Estonia
        '(EL|GR)?[0-9]{9}', // Greece
        '(ES)?[0-9A-Z][0-9]{7}[0-9A-Z]', // Spain
        '(FI)?[0-9]{8}', // Finland
        '(FR)?[0-9A-Z]{2}[0-9]{9}', // France
        '(GB)?([0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3})', // United Kingdom
        '(HU)?[0-9]{8}', // Hungary
        '(IE)?[0-9]S[0-9]{5}L', // Ireland
        '(IT)?[0-9]{11}', // Italy
        '(LT)?([0-9]{9}|[0-9]{12})', // Lithuania
        '(LU)?[0-9]{8}', // Luxembourg
        '(LV)?[0-9]{11}', // Latvia
        '(MT)?[0-9]{8}', // Malta
        '(NL)?[0-9]{9}B[0-9]{2}', // Netherlands
        '(PL)?[0-9]{10}', // Poland
        '(PT)?[0-9]{9}', // Portugal
        '(RO)?[0-9]{2,10}', // Romania
        '(SE)?[0-9]{12}', // Sweden
        '(SI)?[0-9]{8}', // Slovenia
        '(SK)?[0-9]{10}', // Slovakia
    ]
    return new RegExp('^(' + countries.join('|') + ')$').test(text)
}
